import type { AddressSuggestion, FormFieldValidators } from '@jlg-website/shared';

/**
 * Client-side field validators injected into the schema-driven form renderer through
 * `FormConfig.validators`. They fetch the `/api/validators/*` routes from the browser — the
 * same routes the contact form uses for phone and zip — so they never POST to the page
 * route (which the middleware rejects with 405). Each call fails closed: a network or parse
 * error resolves to an invalid/empty result rather than throwing, so the renderer never
 * crashes on a flaky upstream.
 */

export const validatePhone: NonNullable<FormFieldValidators['validatePhone']> = async (phone) => {
  const digits = (phone || '').replace(/\D/g, '');
  if (!digits) return { is_valid: false };

  try {
    const resp = await fetch(`/api/validators/phone?phone=${encodeURIComponent(digits)}`);
    if (!resp.ok) return { is_valid: false };

    const data = await resp.json();
    return { is_valid: data?.is_valid === true };
  } catch {
    return { is_valid: false };
  }
};

export const validatePostalCode: NonNullable<FormFieldValidators['validatePostalCode']> = async (code) => {
  const trimmed = (code || '').trim();
  if (!trimmed) return { is_valid: false };

  try {
    const resp = await fetch(`/api/validators/address?code=${encodeURIComponent(trimmed)}&country=US`);
    if (!resp.ok) return { is_valid: false };

    const data = await resp.json();
    return { is_valid: data?.is_valid === true, city: data?.city, state: data?.state };
  } catch {
    return { is_valid: false };
  }
};

export const searchAddress: NonNullable<FormFieldValidators['searchAddress']> = async (query) => {
  const trimmed = (query || '').trim();
  if (!trimmed) return [];

  try {
    const resp = await fetch(`/api/validators/address/autocomplete?query=${encodeURIComponent(trimmed)}`);
    if (!resp.ok) return [];

    const data = await resp.json();
    return Array.isArray(data) ? (data as AddressSuggestion[]) : [];
  } catch {
    return [];
  }
};
